import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";
import { useUserSettingsContext } from "@/contexts/UserSettingsContext";

const TooltipProvider = TooltipPrimitive.Provider;

type TooltipProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root> & {
  /**
   * Set when the tooltip is a control's name rather than an extra tip: on an
   * icon-only button it is the only visible name, so switching help tips off
   * must not hide it.
   */
  alwaysShow?: boolean;
};

// Wrap Tooltip Root to suppress opening when help tips are switched off.
// This runs in the main React tree (not inside a Radix Portal),
// so the UserSettingsContext is always available.
// When tips are on, pass props through unmodified so Radix
// manages its own open state (uncontrolled). When off, force closed.
const Tooltip = ({ alwaysShow = false, ...props }: TooltipProps) => {
  const { settings } = useUserSettingsContext();

  if (!settings.tooltipsEnabled && !alwaysShow) {
    return <TooltipPrimitive.Root {...props} open={false} />;
  }

  return <TooltipPrimitive.Root {...props} />;
};

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
